import { useState, useMemo, useRef, useEffect } from 'react';
import { Table, DatePicker, Input, Button, Typography, Space, Card, Drawer, Tag, Tooltip, Modal, Descriptions, Spin } from 'antd';
import { SearchOutlined, ApartmentOutlined, PlusSquareOutlined, MinusSquareOutlined, FileTextOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useTrialBalance, useJournalEntries, useAllChartOfAccounts } from '@hooks/useAccounting';
import { useDocumentTypes, useDynamicDocumentByDocumentId } from '@hooks/useMetadata';
import type { TrialBalanceFilters, JournalEntryItem } from '@/types/accounting';
import ReferencePicker from '@components/forms/ReferencePicker';
import {
  acqBuildTrialBalanceTree, acqCollectAllKeys, acqMaxDepth, acqCollectKeysUpToLevel,
  type TrialBalanceNode, type AccountMeta,
} from './trialBalanceTree';

const { RangePicker } = DatePicker;

function formatMoney(acqValue: number): string {
  if (acqValue === 0) return '';
  return acqValue.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function TrialBalancePage() {
  const [acqDateRange, setMdDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().startOf('year'),
    dayjs(),
  ]);
  const [acqAccountCode, setMdAccountCode] = useState('');
  const [acqOrganizationId, setMdOrganizationId] = useState<string | null>(null);
  const [acqFilters, setMdFilters] = useState<TrialBalanceFilters>({
    dateFrom: dayjs().startOf('year').format('YYYY-MM-DD'),
    dateTo: dayjs().format('YYYY-MM-DD'),
  });

  const { data: acqData, isLoading: acqIsLoading } = useTrialBalance(acqFilters);
  const { data: acqAllAccounts } = useAllChartOfAccounts();

  // Мапа code → {parentCode, name} для синтезу батьківських груп у дереві.
  const acqAccountMeta = useMemo<Map<string, AccountMeta>>(() => {
    const acqMap = new Map<string, AccountMeta>();
    if (!acqAllAccounts) return acqMap;
    const acqByIdToCode = new Map<string, string>();
    for (const acqA of acqAllAccounts) acqByIdToCode.set(acqA.id, acqA.code);
    for (const acqA of acqAllAccounts) {
      acqMap.set(acqA.code, {
        code: acqA.code,
        name: acqA.name,
        parentCode: acqA.parentId ? acqByIdToCode.get(acqA.parentId) : undefined,
        isGroup: acqA.isGroup,
      });
    }
    return acqMap;
  }, [acqAllAccounts]);

  const acqHandleGenerate = () => {
    setMdFilters({
      dateFrom: acqDateRange[0].format('YYYY-MM-DD'),
      dateTo: acqDateRange[1].format('YYYY-MM-DD'),
      accountCode: acqAccountCode || undefined,
      organizationId: acqOrganizationId ?? undefined,
    });
  };

  // Дерево рахунків (з синтезом батьківських груп з ChartOfAccount.ParentId).
  const acqTree = useMemo<TrialBalanceNode[]>(
    () => acqBuildTrialBalanceTree(acqData ?? [], acqAccountMeta.size > 0 ? acqAccountMeta : undefined),
    [acqData, acqAccountMeta],
  );
  const acqAllKeys = useMemo(() => acqCollectAllKeys(acqTree), [acqTree]);
  const acqMaxLevel = useMemo(() => acqMaxDepth(acqTree), [acqTree]);
  const [acqExpandedKeys, setMdExpandedKeys] = useState<readonly string[]>([]);
  const [acqUserTouched, setMdUserTouched] = useState(false); // юзер вручну згорнув → не auto-expand more

  // При зміні даних — за замовчуванням розгортаємо все.
  const acqEffectiveExpanded = !acqUserTouched && acqAllKeys.length > 0
    ? acqAllKeys
    : (acqExpandedKeys as string[]);

  const acqExpandToLevel = (acqLevel: number) => {
    setMdUserTouched(true);
    setMdExpandedKeys(acqCollectKeysUpToLevel(acqTree, acqLevel));
  };

  // Drill-down Drawer стан: вибраний рахунок (або null = закрито).
  const [acqDrillAccount, setMdDrillAccount] = useState<string | null>(null);

  // ── Динамічна висота: вимірюємо реальний top кореня (бо MainLayout додає header+nav)
  // та обчислюємо висоту root + scroll-зону таблиці. Без цього 100dvh дає більше за реально доступне.
  const acqRootRef = useRef<HTMLDivElement>(null);
  const acqTableBoxRef = useRef<HTMLDivElement>(null);
  const [acqScrollY, setMdScrollY] = useState<number>(400);

  useEffect(() => {
    const acqRecompute = () => {
      if (!acqRootRef.current) return;
      const acqTop = acqRootRef.current.getBoundingClientRect().top;
      // 24px нижній буфер: компенсує border + padding parent layout, щоб точно не було overflow.
      const acqAvailable = window.innerHeight - acqTop - 24;
      acqRootRef.current.style.height = `${acqAvailable}px`;
      // scroll.y таблиці = висота table-box − ~160px (групований header антд 75px + summary 38px + borders + bottom-padding).
      if (acqTableBoxRef.current) {
        const acqBoxH = acqTableBoxRef.current.clientHeight;
        setMdScrollY(Math.max(200, acqBoxH - 160));
      }
    };
    acqRecompute();
    window.addEventListener('resize', acqRecompute);
    const acqRo = new ResizeObserver(acqRecompute);
    if (acqRootRef.current) acqRo.observe(acqRootRef.current);
    return () => { window.removeEventListener('resize', acqRecompute); acqRo.disconnect(); };
  }, []);

  // Totals (по плоскому списку — backend повертає кожен рахунок окремо, без overlap).
  const acqTotals = useMemo(() => {
    if (!acqData?.length) return null;
    return {
      openingDebit: acqData.reduce((acqS, acqR) => acqS + acqR.openingDebit, 0),
      openingCredit: acqData.reduce((acqS, acqR) => acqS + acqR.openingCredit, 0),
      turnoverDebit: acqData.reduce((acqS, acqR) => acqS + acqR.turnoverDebit, 0),
      turnoverCredit: acqData.reduce((acqS, acqR) => acqS + acqR.turnoverCredit, 0),
      closingDebit: acqData.reduce((acqS, acqR) => acqS + acqR.closingDebit, 0),
      closingCredit: acqData.reduce((acqS, acqR) => acqS + acqR.closingCredit, 0),
    };
  }, [acqData]);

  // Helper для рендеру клікабельної суми — клік відкриває drill-down drawer проводок цього рахунку.
  // Нульові суми не клікабельні (нічого показувати).
  const acqRenderDrillSum = (acqVal: number, acqRow: TrialBalanceNode, acqStyle?: React.CSSProperties) => {
    const acqText = formatMoney(acqVal);
    if (!acqVal) return <span style={{ color: '#ccc' }}>{acqText}</span>;
    return (
      <Tooltip title={`Показати проводки по рахунку ${acqRow.accountCode}`} mouseEnterDelay={0.4}>
        <a
          onClick={(e) => { e.stopPropagation(); setMdDrillAccount(acqRow.accountCode); }}
          style={acqStyle}
        >
          {acqText}
        </a>
      </Tooltip>
    );
  };

  const acqColumns: ColumnsType<TrialBalanceNode> = [
    {
      title: 'Рахунок',
      dataIndex: 'accountCode',
      key: 'accountCode',
      width: 140,
      render: (acqVal: string, acqRow: TrialBalanceNode) => (
        <Space size={4}>
          <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{acqVal}</span>
          {acqRow.descendantCount > 0 && (
            <Tag color="blue" style={{ marginLeft: 4, fontSize: 10 }}>
              +{acqRow.descendantCount}
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Назва рахунку',
      dataIndex: 'accountName',
      key: 'accountName',
      ellipsis: true,
      render: (acqVal: string, acqRow: TrialBalanceNode) => (
        <Tooltip title={acqRow.isSynthetic ? 'Група рахунків (агрегована сума)' : 'Клік — показати проводки за цим рахунком'}>
          <a
            onClick={(e) => { e.stopPropagation(); setMdDrillAccount(acqRow.accountCode); }}
            style={acqRow.isSynthetic ? { fontWeight: 600, fontStyle: 'italic' } : undefined}
          >
            {acqVal}
          </a>
        </Tooltip>
      ),
    },
    {
      title: 'Сальдо на початок',
      children: [
        {
          title: 'Дебет', dataIndex: 'openingDebit', key: 'openingDebit', width: 130, align: 'right' as const,
          render: (acqVal: number, acqRow: TrialBalanceNode) => acqRenderDrillSum(acqVal, acqRow, { color: '#000' }),
        },
        {
          title: 'Кредит', dataIndex: 'openingCredit', key: 'openingCredit', width: 130, align: 'right' as const,
          render: (acqVal: number, acqRow: TrialBalanceNode) => acqRenderDrillSum(acqVal, acqRow, { color: '#000' }),
        },
      ],
    },
    {
      title: 'Обороти за період',
      children: [
        {
          title: 'Дебет', dataIndex: 'turnoverDebit', key: 'turnoverDebit', width: 140, align: 'right' as const,
          render: (acqVal: number, acqRow: TrialBalanceNode) => acqRenderDrillSum(acqVal, acqRow, { color: '#1677ff', fontWeight: 500 }),
        },
        {
          title: 'Кредит', dataIndex: 'turnoverCredit', key: 'turnoverCredit', width: 140, align: 'right' as const,
          render: (acqVal: number, acqRow: TrialBalanceNode) => acqRenderDrillSum(acqVal, acqRow, { color: '#1677ff', fontWeight: 500 }),
        },
      ],
    },
    {
      title: 'Сальдо на кінець',
      children: [
        {
          title: 'Дебет', dataIndex: 'closingDebit', key: 'closingDebit', width: 140, align: 'right' as const,
          render: (acqVal: number, acqRow: TrialBalanceNode) => acqRenderDrillSum(acqVal, acqRow, { color: '#389e0d', fontWeight: 600 }),
        },
        {
          title: 'Кредит', dataIndex: 'closingCredit', key: 'closingCredit', width: 140, align: 'right' as const,
          render: (acqVal: number, acqRow: TrialBalanceNode) => acqRenderDrillSum(acqVal, acqRow, { color: '#cf1322', fontWeight: 600 }),
        },
      ],
    },
  ];

  // Кнопки рівнів — генеруємо динамічно: 1, 2, ..., maxLevel+1.
  const acqLevelButtons = Array.from({ length: acqMaxLevel + 1 }, (_, i) => i + 1);

  return (
    // Повноекранний layout: висота міряється useEffect-ом (acqRootRef.style.height = window.innerHeight − top).
    <div ref={acqRootRef} style={{
      display: 'flex', flexDirection: 'column',
      overflow: 'hidden',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexShrink: 0 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>Оборотно-сальдова відомість</Typography.Title>
      </div>

      <Card size="small" style={{ marginBottom: 12, flexShrink: 0 }}>
        <Space wrap>
          <span>Період:</span>
          <RangePicker
            value={acqDateRange}
            onChange={(acqDates) => {
              if (acqDates && acqDates[0] && acqDates[1]) {
                setMdDateRange([acqDates[0], acqDates[1]]);
              }
            }}
            format="DD.MM.YYYY"
          />
          <span>Організація:</span>
          <div style={{ width: 240 }}>
            <ReferencePicker
              acqReferenceType="organization"
              acqValue={acqOrganizationId}
              acqOnChange={(acqId) => setMdOrganizationId(acqId)}
              acqAllowClear
              acqSize="middle"
            />
          </div>
          <span>Рахунок:</span>
          <Input
            placeholder="напр. 31 або 311"
            value={acqAccountCode}
            onChange={(acqE) => setMdAccountCode(acqE.target.value)}
            style={{ width: 150 }}
            allowClear
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={acqHandleGenerate} loading={acqIsLoading}>
            Сформувати
          </Button>
        </Space>

        {acqMaxLevel > 0 && (
          <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>Рівні дерева:</Typography.Text>
            <Tooltip title="Згорнути все (показати лише корені)">
              <Button
                size="small"
                icon={<MinusSquareOutlined />}
                onClick={() => acqExpandToLevel(0)}
              >
                Згорнути все
              </Button>
            </Tooltip>
            {acqLevelButtons.map((acqLvl) => (
              <Tooltip key={acqLvl} title={`Розгорнути до рівня ${acqLvl}`}>
                <Button size="small" onClick={() => acqExpandToLevel(acqLvl)}>
                  {acqLvl}
                </Button>
              </Tooltip>
            ))}
            <Tooltip title="Розгорнути все">
              <Button
                size="small"
                icon={<PlusSquareOutlined />}
                onClick={() => acqExpandToLevel(999)}
              >
                Розгорнути все
              </Button>
            </Tooltip>
          </div>
        )}
      </Card>

      {/* Таблиця — займає решту висоти, з власним скролом. scroll.y = box.clientHeight − ~140px. */}
      <div ref={acqTableBoxRef} style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Table<TrialBalanceNode>
          columns={acqColumns}
          dataSource={acqTree}
          rowKey="accountCode"
          loading={acqIsLoading}
          pagination={false}
          bordered
          size="small"
          sticky
          scroll={{ y: acqScrollY, x: 'max-content' }}
          expandable={{
            expandedRowKeys: acqEffectiveExpanded,
            onExpandedRowsChange: (acqKeys) => { setMdUserTouched(true); setMdExpandedKeys(acqKeys as string[]); },
            indentSize: 18,
            rowExpandable: (acqRow) => Boolean(acqRow.children?.length),
            expandIcon: ({ expanded, onExpand, record, expandable }) => {
              if (!expandable) {
                return <span style={{ display: 'inline-block', width: 18 }} />;
              }
              return expanded ? (
                <MinusSquareOutlined
                  style={{ fontSize: 14, color: '#1677ff', cursor: 'pointer' }}
                  onClick={(e) => onExpand(record, e)}
                />
              ) : (
                <PlusSquareOutlined
                  style={{ fontSize: 14, color: '#1677ff', cursor: 'pointer' }}
                  onClick={(e) => onExpand(record, e)}
                />
              );
            },
          }}
          summary={() =>
            acqTotals ? (
              <Table.Summary fixed>
                <Table.Summary.Row style={{ background: '#fafafa', fontWeight: 700 }}>
                  <Table.Summary.Cell index={0}>Разом</Table.Summary.Cell>
                  <Table.Summary.Cell index={1} />
                  <Table.Summary.Cell index={2} align="right">{formatMoney(acqTotals.openingDebit)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="right">{formatMoney(acqTotals.openingCredit)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={4} align="right">{formatMoney(acqTotals.turnoverDebit)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={5} align="right">{formatMoney(acqTotals.turnoverCredit)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={6} align="right">{formatMoney(acqTotals.closingDebit)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={7} align="right">{formatMoney(acqTotals.closingCredit)}</Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            ) : undefined
          }
        />
      </div>

      <AccountDrillDownDrawer
        acqAccountCode={acqDrillAccount}
        acqDateFrom={acqFilters.dateFrom}
        acqDateTo={acqFilters.dateTo}
        acqOrganizationId={acqFilters.organizationId}
        acqOnClose={() => setMdDrillAccount(null)}
      />
    </div>
  );
}

// ─────────────────────── Document Viewer Modal ───────────────────────
// Невелика read-only модалка для перегляду документа з drill-down drawer'а ОСВ.

interface DocViewerProps {
  acqDocumentId: string | null;
  acqDocTypeName?: string;
  acqOnClose: () => void;
}

function DocumentViewerModal({ acqDocumentId, acqDocTypeName, acqOnClose }: DocViewerProps) {
  const acqOpen = acqDocumentId !== null;
  // У JournalEntry зберігається лише Document.Id, тому використовуємо by-document/{docId} (Phase 8b).
  const { data: acqDoc, isLoading } = useDynamicDocumentByDocumentId(acqDocumentId ?? undefined);

  return (
    <Modal
      open={acqOpen}
      onCancel={acqOnClose}
      footer={null}
      width={760}
      title={
        <Space>
          <FileTextOutlined />
          <span>{acqDocTypeName ?? 'Документ'}</span>
        </Space>
      }
    >
      {isLoading && <Spin />}
      {acqDoc && (
        <Descriptions bordered size="small" column={2}>
          <Descriptions.Item label="№">{acqDoc.docNumber ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="Дата">{dayjs(acqDoc.docDate).format('DD.MM.YYYY')}</Descriptions.Item>
          <Descriptions.Item label="Тип">{acqDocTypeName ?? acqDoc.docTypeCode}</Descriptions.Item>
          <Descriptions.Item label="Організація">{(acqDoc as { organizationCode?: string }).organizationCode ?? acqDoc.organizationId ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="Сума">{acqDoc.totalAmount?.toLocaleString('uk-UA', { minimumFractionDigits: 2 })}</Descriptions.Item>
          <Descriptions.Item label="Стан">
            {acqDoc.isPosted ? <Tag color="green">Проведений</Tag> : <Tag>Не проведений</Tag>}
          </Descriptions.Item>
          {acqDoc.comment && <Descriptions.Item label="Коментар" span={2}>{acqDoc.comment}</Descriptions.Item>}
          <Descriptions.Item label="Реквізити (JSONB)" span={2}>
            <pre style={{ maxHeight: 280, overflow: 'auto', background: '#f5f5f5', padding: 8, fontSize: 11, margin: 0 }}>
              {(() => {
                try { return JSON.stringify(JSON.parse(acqDoc.dataJson), null, 2); }
                catch { return acqDoc.dataJson; }
              })()}
            </pre>
          </Descriptions.Item>
        </Descriptions>
      )}
      {!isLoading && !acqDoc && acqDocumentId && (
        <Typography.Text type="secondary">
          Документ {acqDocumentId} не знайдено або імпортовано лише як шапку (без DynamicDocument).
        </Typography.Text>
      )}
    </Modal>
  );
}

// ─────────────────────── Drill-down Drawer ───────────────────────

interface DrillProps {
  acqAccountCode: string | null;
  acqDateFrom: string;
  acqDateTo: string;
  acqOrganizationId?: string;
  acqOnClose: () => void;
}

function AccountDrillDownDrawer({ acqAccountCode, acqDateFrom, acqDateTo, acqOrganizationId, acqOnClose }: DrillProps) {
  const acqOpen = acqAccountCode !== null;
  const [acqPage, setMdPage] = useState(1);
  const [acqPageSize, setMdPageSize] = useState(50);
  const [acqViewerDocId, setMdViewerDocId] = useState<string | null>(null);

  const { data: acqData, isLoading } = useJournalEntries({
    accountCode: acqAccountCode ?? undefined,
    dateFrom: acqDateFrom,
    dateTo: acqDateTo,
    organizationId: acqOrganizationId,
    page: acqPage,
    pageSize: acqPageSize,
  });

  // Резолв snake_case docTypeCode → людська назва (з MetaDocumentTypes).
  const { data: acqDocTypes } = useDocumentTypes();
  const acqDocTypeNames = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of acqDocTypes ?? []) m.set(t.code, t.name);
    return m;
  }, [acqDocTypes]);

  const acqItems = acqData?.items ?? [];
  const acqTotal = acqData?.totalCount ?? 0;
  const acqViewerDocTypeName = (() => {
    if (!acqViewerDocId) return undefined;
    const acqRow = acqItems.find((i) => i.documentId === acqViewerDocId);
    return acqRow?.docType ? acqDocTypeNames.get(acqRow.docType) ?? acqRow.docType : undefined;
  })();

  const acqColumns: ColumnsType<JournalEntryItem> = [
    {
      title: 'Дата', dataIndex: 'entryDate', key: 'entryDate', width: 100,
      render: (acqVal: string) => dayjs(acqVal).format('DD.MM.YYYY'),
    },
    {
      title: 'Організація', key: 'org', width: 180, ellipsis: true,
      render: (_: unknown, acqRow: JournalEntryItem) => acqRow.organizationCode ? (
        <Tooltip title={`code: ${acqRow.organizationCode}`}>
          <Tag icon={<ApartmentOutlined />} style={{ margin: 0 }}>
            {acqRow.organizationName ?? acqRow.organizationCode}
          </Tag>
        </Tooltip>
      ) : <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: 'Документ', dataIndex: 'docNumber', key: 'doc', width: 280, ellipsis: true,
      render: (acqVal: string | undefined, acqRow: JournalEntryItem) => {
        const acqTypeName = acqRow.docType ? (acqDocTypeNames.get(acqRow.docType) ?? acqRow.docType) : null;
        return (
          <Tooltip title="Відкрити документ" mouseEnterDelay={0.4}>
            <a
              onClick={() => setMdViewerDocId(acqRow.documentId)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              {acqTypeName && <Tag style={{ margin: 0 }}>{acqTypeName}</Tag>}
              <span>{acqVal ?? '—'}</span>
            </a>
          </Tooltip>
        );
      },
    },
    {
      title: 'Дт', dataIndex: 'debitAccount', key: 'dt', width: 80,
      render: (acqVal: string) => (
        <span style={{
          fontFamily: 'monospace', fontWeight: 600,
          color: acqAccountCode && acqVal.startsWith(acqAccountCode) ? '#1677ff' : undefined,
        }}>{acqVal}</span>
      ),
    },
    {
      title: 'Кт', dataIndex: 'creditAccount', key: 'ct', width: 80,
      render: (acqVal: string) => (
        <span style={{
          fontFamily: 'monospace', fontWeight: 600,
          color: acqAccountCode && acqVal.startsWith(acqAccountCode) ? '#cf1322' : undefined,
        }}>{acqVal}</span>
      ),
    },
    {
      title: 'Сума', dataIndex: 'amount', key: 'amount', width: 130, align: 'right' as const,
      render: (acqVal: number) => acqVal.toLocaleString('uk-UA', { minimumFractionDigits: 2 }),
    },
    {
      title: 'Опис', dataIndex: 'description', key: 'desc', ellipsis: true,
    },
  ];

  return (
    <Drawer
      open={acqOpen}
      onClose={acqOnClose}
      width={1100}
      title={
        <Space>
          <span>Проводки по рахунку</span>
          {acqAccountCode && <Tag color="blue" style={{ fontSize: 14 }}>{acqAccountCode}</Tag>}
          <Typography.Text type="secondary">
            {dayjs(acqDateFrom).format('DD.MM.YYYY')} — {dayjs(acqDateTo).format('DD.MM.YYYY')}
          </Typography.Text>
        </Space>
      }
      destroyOnClose
    >
      <Table<JournalEntryItem>
        columns={acqColumns}
        dataSource={acqItems}
        rowKey="id"
        loading={isLoading}
        size="small"
        bordered
        pagination={{
          current: acqPage,
          pageSize: acqPageSize,
          total: acqTotal,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100'],
          onChange: (acqP, acqPs) => { setMdPage(acqP); setMdPageSize(acqPs); },
          showTotal: (acqT) => `Всього: ${acqT}`,
        }}
      />
      <DocumentViewerModal
        acqDocumentId={acqViewerDocId}
        acqDocTypeName={acqViewerDocTypeName}
        acqOnClose={() => setMdViewerDocId(null)}
      />
    </Drawer>
  );
}
